import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { AccountsNumberColumnComponent } from '@nk/company-block';
import {
  AppIdentityCompositeEntity,
  ScreeningConfigCompositeChanges,
  ScreeningConfigCompositeEntity,
  ScreeningConfigEntity,
  ScreeningConfigPartialCompositeEntity,
  ScreeningConfigScreeningListCategoryEntity,
  ScreeningConfigScreeningListCategoryPartialEntity,
  ScreeningConfigWatchlistEntity,
  ScreeningConfigWatchlistPartialEntity,
  WatchlistCompanyAggregateEntity,
  WatchlistEntity,
} from '@nk/data-entities-lib';
import { LinkColumnComponent, LinkColumnItem, NkDatatableColumn } from '@nk/ui-lib';
import { arrayToDict, ClassMethod, Dict } from '@nk/utils-lib';
import { forkJoin, iif, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ScreeningConfigService } from '../services/screening-config.service';

export const SCREENING_CONFIG_EDIT_ROUTE = 'screening-config/:screeningConfigUuid';

export interface ScreeningConfigWatchlistsTableItem {
  uuid: string;
  watchlistName: LinkColumnItem;
  numberOfCompanies: number;
}
export interface ScreeningConfigFormConfig {
  watchlist_uuids: string[];
  screening_list_category_uuids: string[];
  auto_approve_monitoring_flag: boolean;
  subscribe_to_email_notifications_flag: boolean;
  screening_config_system_suggestion_setting_uuid: string;
}

export const SCREENING_CONFIG_WATCHLISTS_TABLE_CONFIG: NkDatatableColumn<ScreeningConfigWatchlistsTableItem>[] = [
  {
    prop: 'watchlistName',
    name: extractTranslation('screening.screening_config.table.watchlist_name'),
    component: LinkColumnComponent,
    sortable: false,
  },
  {
    prop: 'numberOfCompanies',
    name: extractTranslation('screening.screening_config.table.number_of_companies'),
    component: AccountsNumberColumnComponent,
    sortable: false,
  },
];

export function getSelectedEntities(
  watchlistUuids: string[],
  allEntities: ScreeningConfigWatchlistsTableItem[],
): ScreeningConfigWatchlistsTableItem[] {
  const dict = arrayToDict(allEntities || [], (i) => i.uuid);
  return (watchlistUuids || []).reduce((res: ScreeningConfigWatchlistsTableItem[], i) => {
    if (dict[i]) {
      res.push(dict[i]);
    }
    return res;
  }, []);
}

export function filterSharedWatchlists(
  items: WatchlistEntity[],
  identity: AppIdentityCompositeEntity,
): WatchlistEntity[] {
  return items.filter((x) => x.app_customer_uuid === identity.app_customer?.uuid && !x.personal_flag);
}

export function getScreeningConfigWatchlistsTableItem(
  watchlist: WatchlistEntity,
  watchlistCompanyAggregateDict: Dict<WatchlistCompanyAggregateEntity>,
): ScreeningConfigWatchlistsTableItem {
  return {
    uuid: watchlist.uuid as string,
    watchlistName: {
      label: watchlist.name,
      routerLink: `/watchlist/${watchlist.uuid}`,
    },
    numberOfCompanies: watchlistCompanyAggregateDict[watchlist.uuid as string]?.company_count || 0,
  };
}

export function getScreeningConfigCompositeChanges(
  screeningConfigComposite: ScreeningConfigCompositeEntity,
  formValue: ScreeningConfigFormConfig,
): ScreeningConfigCompositeChanges {
  if (!screeningConfigComposite || !formValue) {
    throw new Error('getScreeningConfigCompositeChanges: wrong input parameters');
  }
  const screeningConfig: ScreeningConfigEntity = {
    ...screeningConfigComposite.screening_config,
    auto_approve_monitoring_flag: formValue.auto_approve_monitoring_flag,
    subscribe_to_email_notifications_flag: formValue.subscribe_to_email_notifications_flag,
    screening_config_system_suggestion_setting_uuid: formValue.screening_config_system_suggestion_setting_uuid,
  };

  const currentScreeningConfigWatchlistsDict = arrayToDict(
    screeningConfigComposite.screening_config_watchlists,
    (i) => i.watchlist_uuid,
  );

  const currentScreeningConfigScreeningLisCategorytDict = arrayToDict(
    screeningConfigComposite.screening_config_screening_list_categories,
    (i) => i.screening_list_category_uuid,
  );

  const screeningConfigWatchlistsCreateList: ScreeningConfigWatchlistPartialEntity[] = formValue.watchlist_uuids
    .filter((i) => !currentScreeningConfigWatchlistsDict[i])
    .map((i) => ({ watchlist_uuid: i }));

  const screeningConfigWatchlistsDeleteList: string[] = Object.keys(currentScreeningConfigWatchlistsDict)
    .filter((i) => !formValue.watchlist_uuids.includes(i))
    .map((i) => {
      return currentScreeningConfigWatchlistsDict[i].uuid as string;
    });

  const screeningConfigScreeningListCategoryCreateList: ScreeningConfigScreeningListCategoryPartialEntity[] =
    formValue.screening_list_category_uuids
      .filter(
        (screeningListCategoryUuid) => !currentScreeningConfigScreeningLisCategorytDict[screeningListCategoryUuid],
      )
      .map((screeningListCategoryUuid) => ({ screening_list_category_uuid: screeningListCategoryUuid }));

  const screeningConfigScreeningListCategoryDeleteList: string[] = Object.keys(
    currentScreeningConfigScreeningLisCategorytDict,
  )
    .filter((screeningListCategoryUuid) => !formValue.screening_list_category_uuids.includes(screeningListCategoryUuid))
    .map((screeningListCategoryUuid) => {
      return currentScreeningConfigScreeningLisCategorytDict[screeningListCategoryUuid].uuid as string;
    });

  return {
    screeningConfig,
    screeningConfigWatchlists: {
      createList: screeningConfigWatchlistsCreateList,
      deleteList: screeningConfigWatchlistsDeleteList,
    },
    screeningConfigScreeningListCategory: {
      createList: screeningConfigScreeningListCategoryCreateList,
      deleteList: screeningConfigScreeningListCategoryDeleteList,
    },
  };
}

/*
 * This method posts a screening config and sends post requests to create its watchlist list
 */
export function postScreeningConfigCompositeObservable(
  payload: ScreeningConfigPartialCompositeEntity,
  postScreeningConfigMethod: ClassMethod<ScreeningConfigService, 'postScreeningConfig'>,
  postScreeningConfigWatchlistMethod: ClassMethod<ScreeningConfigService, 'postScreeningConfigWatchlist'>,
  postScreeningConfigScreeningListCategoryMethod: ClassMethod<
    ScreeningConfigService,
    'postScreeningConfigScreeningListCategory'
  >,
): Observable<ScreeningConfigCompositeEntity> {
  if (
    !payload ||
    !payload.screening_config ||
    !payload.screening_config_watchlists.length ||
    !payload.screening_config_screening_list_categories ||
    !postScreeningConfigMethod ||
    !postScreeningConfigWatchlistMethod ||
    !postScreeningConfigScreeningListCategoryMethod
  ) {
    return throwError(() => 'postScreeningConfigCompositeObservable: wrong import parameters');
  }
  const categories = payload.screening_config_screening_list_categories;
  return postScreeningConfigMethod(payload.screening_config).pipe(
    switchMap((screeningConfig) =>
      forkJoin([
        of(screeningConfig),
        forkJoin(
          payload.screening_config_watchlists.map((i) =>
            postScreeningConfigWatchlistMethod(screeningConfig.uuid as string, i),
          ),
        ),
        categories?.length
          ? forkJoin(
              categories.map((i) => postScreeningConfigScreeningListCategoryMethod(screeningConfig.uuid as string, i)),
            )
          : of([]),
      ]),
    ),
    map((data) => {
      const [screeningConfig, screeningConfigWatchlists, screeningConfigScreeningListCategories] = data;
      return {
        uuid: screeningConfig.uuid as string,
        screening_config: screeningConfig,
        screening_config_watchlists: screeningConfigWatchlists,
        screening_config_screening_list_categories: screeningConfigScreeningListCategories,
      };
    }),
  );
}

/*
 * This method updates screening config and sends post and delete requests to update its watchlist list
 * On success returns just true, to re-fetch the entire composite use {@link screeningConfigComposite}
 */
export function updateScreeningConfigCompositeObservable(
  screeningConfigCompositeChanges: ScreeningConfigCompositeChanges,
  patchScreeningConfigMethod: ClassMethod<ScreeningConfigService, 'patchScreeningConfig'>,
  postScreeningConfigWatchlistMethod: ClassMethod<ScreeningConfigService, 'postScreeningConfigWatchlist'>,
  deleteScreeningConfigWatchlistMethod: ClassMethod<ScreeningConfigService, 'deleteScreeningConfigWatchlist'>,
  postScreeningConfigScreeningListCategoryMethod: ClassMethod<
    ScreeningConfigService,
    'postScreeningConfigScreeningListCategory'
  >,
  deleteScreeningConfigScreeningListCategoryMethod: ClassMethod<
    ScreeningConfigService,
    'deleteScreeningConfigScreeningListCategory'
  >,
): Observable<true> {
  if (
    !screeningConfigCompositeChanges ||
    !patchScreeningConfigMethod ||
    !postScreeningConfigWatchlistMethod ||
    !deleteScreeningConfigWatchlistMethod ||
    !postScreeningConfigScreeningListCategoryMethod ||
    !deleteScreeningConfigScreeningListCategoryMethod
  ) {
    return throwError(() => 'updateScreeningConfigCompositeObservable: wrong import parameters');
  }
  return patchScreeningConfigMethod(
    screeningConfigCompositeChanges.screeningConfig.uuid as string,
    screeningConfigCompositeChanges.screeningConfig,
  ).pipe(
    switchMap(() =>
      iif(
        () => !!screeningConfigCompositeChanges.screeningConfigWatchlists.createList.length,
        forkJoin(
          screeningConfigCompositeChanges.screeningConfigWatchlists.createList.map((i) =>
            postScreeningConfigWatchlistMethod(screeningConfigCompositeChanges.screeningConfig.uuid as string, i),
          ),
        ),
        of<ScreeningConfigWatchlistEntity[]>([]),
      ),
    ),
    switchMap(() =>
      iif(
        () => !!screeningConfigCompositeChanges.screeningConfigWatchlists.deleteList.length,
        forkJoin(
          screeningConfigCompositeChanges.screeningConfigWatchlists.deleteList.map((screeningConfigWatchlistUuid) =>
            deleteScreeningConfigWatchlistMethod(
              screeningConfigCompositeChanges.screeningConfig.uuid as string,
              screeningConfigWatchlistUuid,
            ),
          ),
        ),
        of<ScreeningConfigWatchlistEntity[]>([]),
      ),
    ),
    switchMap(() =>
      iif(
        () => !!screeningConfigCompositeChanges.screeningConfigScreeningListCategory.createList.length,
        forkJoin(
          screeningConfigCompositeChanges.screeningConfigScreeningListCategory.createList.map((i) =>
            postScreeningConfigScreeningListCategoryMethod(
              screeningConfigCompositeChanges.screeningConfig.uuid as string,
              i,
            ),
          ),
        ),
        of<ScreeningConfigScreeningListCategoryEntity[]>([]),
      ),
    ),
    switchMap(() =>
      iif(
        () => !!screeningConfigCompositeChanges.screeningConfigScreeningListCategory.deleteList.length,
        forkJoin(
          screeningConfigCompositeChanges.screeningConfigScreeningListCategory.deleteList.map(
            (screeningListCategoryUuid) =>
              deleteScreeningConfigScreeningListCategoryMethod(
                screeningConfigCompositeChanges.screeningConfig.uuid as string,
                screeningListCategoryUuid,
              ),
          ),
        ),
        of<ScreeningConfigScreeningListCategoryEntity[]>([]),
      ),
    ),
    map(() => {
      return true;
    }),
  );
}
